import { useState, type FC } from "react"
import { Navigate, useParams, useLocation } from "react-router"
import styled from "styled-components"

import { parseNumber } from "@forento/shared/utilities/number"

import { usePlatform } from "~/contexts/PlatformContext"
import { useUser } from "~/contexts/UserContext"
import Button from "~/themes/school/components/Button"
import Hero from "~/themes/school/components/Hero"
import Layout from "~/themes/school/components/Layout"
import PageHeader, { PageBreadcrumb } from "~/themes/school/components/PageHeader"
import PartialLoadingPage from "~/themes/school/components/PartialLoadingPage"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import PriceButton from "./PriceButton"

const MembershipPage: FC = () => {
	const membershipId = parseNumber(useParams().membershipId)
	const platform = usePlatform().platform
	const user = useUser()
	const t = useTranslation()
	const location = useLocation()

	const [submittingPriceId, setSubmittingPriceId] = useState<number>()

	const { data: membership, error } = query.membership.getPublic.useQuery(membershipId ?? -1)

	const billingPortalUrl = query.payment.getMembershipBillingPortalUrl.useMutation({
		onSuccess: url => window.location.assign(url),
		onError: error => console.error(error),
	})

	if (membership === null) return <Navigate to={routes.membership.index()} replace />

	const isActive =
		membership !== undefined &&
		user.user?.membership?.id === membership.id &&
		(user.user.membership.endDate === null || user.user.membership.endDate > new Date())

	return (
		<Layout>
			<PageHeader>
				<PageBreadcrumb
					title={membership?.title ?? "..."}
					path={[{ title: t("membership.title"), link: routes.membership.index() }]}
				/>
			</PageHeader>

			{error ? (
				<p>{t("failedToLoad")}</p>
			) : !membership ? (
				<PartialLoadingPage />
			) : (
				<Hero
					title={membership.title}
					htmlDescription={membership.longDescription?.html}
					thumbnailUrl={membership.thumbnailFilePath}
					callToAction={
						user.user?.isCreator ? (
							<CreatorWarning>{t("membership.isCreator")}</CreatorWarning>
						) : (
							<CallToActions>
								{membership.prices.map(price => (
									<PriceButton
										key={price.id}
										membershipId={membership.id}
										isActiveMembership={isActive}
										price={price}
										isFull={membership.isFull}
										setSubmittingPriceId={setSubmittingPriceId}
										submittingPriceId={submittingPriceId}
									/>
								))}
								{isActive && platform.paymentGateway?.type === "stripe" && (
									<Button
										variant="secondary"
										isLoading={billingPortalUrl.isPending}
										onClick={() => billingPortalUrl.mutate({ returnUrl: location.pathname })}
									>
										{t("billing.updatePaymentMethod")}
									</Button>
								)}
							</CallToActions>
						)
					}
				/>
			)}
		</Layout>
	)
}

const CreatorWarning = styled.div`
	font-style: italic;
`

const CallToActions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`

export default MembershipPage
